import { useState, useEffect } from 'react';
import { useParams } from 'react-router';
import styles from './ApplyForm.module.css';

interface PropertyInfo {
  name: string;
  address: string;
  layout: string;
  rent: number;
}

const API = import.meta.env.VITE_API_URL || '/api';

const STEPS = [
  { key: 'basic', title: '基本情報', icon: '👤' },
  { key: 'work', title: '勤務先情報', icon: '🏢' },
  { key: 'guarantor', title: '連帯保証人', icon: '🤝' },
  { key: 'move', title: '入居情報', icon: '🏠' },
];

const INCOME_OPTIONS = ['', '200万円未満', '200〜300万円', '300〜400万円', '400〜500万円', '500〜600万円', '600〜800万円', '800〜1000万円', '1000万円以上'];

export function ApplyForm() {
  const { token } = useParams<{ token: string }>();
  const [property, setProperty] = useState<PropertyInfo | null>(null);
  const [formId, setFormId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [form, setForm] = useState({
    name: '', name_kana: '', birth_date: '', phone: '', email: '',
    current_address: '',
    company_name: '', company_address: '', company_phone: '',
    annual_income: '', years_employed: '',
    guarantor_name: '', guarantor_address: '', guarantor_phone: '', guarantor_company: '',
    move_in_date: '', num_residents: '1',
    notes: '',
  });

  // localStorage一時保存
  useEffect(() => {
    const saved = localStorage.getItem(`apply_${token}`);
    if (saved) { try { setForm(JSON.parse(saved)); } catch { /* ignore */ } }
  }, [token]);

  useEffect(() => {
    if (token && form.name) localStorage.setItem(`apply_${token}`, JSON.stringify(form));
  }, [form, token]);

  useEffect(() => {
    if (!token) return;
    fetch(`${API}/public/apply/${token}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => { setProperty(data.property); setFormId(data.form_id); setLoading(false); })
      .catch(() => { setError('フォームが見つかりません。URLを確認してください。'); setLoading(false); });
  }, [token]);

  const update = (key: string, value: string) => {
    setForm(f => ({ ...f, [key]: value }));
    if (errors[key]) setErrors(e => { const n = { ...e }; delete n[key]; return n; });
  };

  // 電話番号フォーマット
  const formatPhone = (val: string) => {
    const digits = val.replace(/[^\d]/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7, 11)}`;
  };

  const handlePhoneChange = (key: string, val: string) => {
    update(key, formatPhone(val));
  };

  // ステップ別バリデーション
  const validateStep = (s: number): boolean => {
    const errs: Record<string, string> = {};
    if (s === 0) {
      if (!form.name.trim()) errs.name = '氏名は必須です';
      if (!form.phone.trim()) errs.phone = '電話番号は必須です';
      if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'メールアドレスの形式が正しくありません';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (validateStep(step)) setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const handleBack = () => setStep(s => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    if (!validateStep(step)) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API}/public/apply/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setSubmitted(true);
      localStorage.removeItem(`apply_${token}`);
    } catch {
      setError('送信に失敗しました。しばらく経ってからお試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className={styles.page}><div className={styles.card}><div className={styles.loadingText}>読み込み中...</div></div></div>;

  if (submitted) return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.successIcon}>✅</div>
        <h2 className={styles.successTitle}>お申込みありがとうございます</h2>
        <p className={styles.successDesc}>内容を確認後、担当者よりご連絡いたします。</p>
      </div>
    </div>
  );

  const isLast = step === STEPS.length - 1;

  const renderField = (key: string, label: string, opts?: { type?: string; required?: boolean; placeholder?: string; phone?: boolean }) => (
    <div className={styles.field}>
      <label className={styles.label}>{label} {opts?.required && <span className={styles.req}>*</span>}</label>
      <input
        className={`${styles.input} ${errors[key] ? styles.inputError : ''}`}
        type={opts?.type || 'text'}
        value={(form as any)[key]}
        onChange={e => opts?.phone ? handlePhoneChange(key, e.target.value) : update(key, e.target.value)}
        placeholder={opts?.placeholder}
      />
      {errors[key] && <div className={styles.fieldError}>{errors[key]}</div>}
    </div>
  );

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <h1 className={styles.title}>入居申込書</h1>

        {property && (
          <div className={styles.propertyInfo}>
            <div className={styles.propertyName}>{property.name}</div>
            <div className={styles.propertyMeta}>{property.address} / {property.layout} / ¥{property.rent?.toLocaleString()}/月</div>
          </div>
        )}

        {/* 進捗バー */}
        <div className={styles.progressBar}>
          {STEPS.map((s, i) => (
            <div key={s.key} className={`${styles.progressStep} ${i <= step ? styles.progressActive : ''} ${i < step ? styles.progressDone : ''}`}>
              <div className={styles.progressDot}>{i < step ? '✓' : s.icon}</div>
              <div className={styles.progressLabel}>{s.title}</div>
            </div>
          ))}
          <div className={styles.progressLine}>
            <div className={styles.progressFill} style={{ width: `${(step / (STEPS.length - 1)) * 100}%` }} />
          </div>
        </div>

        {error && <div className={styles.error}>{error}</div>}

        {/* Step 1: 基本情報 */}
        {step === 0 && (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>👤 基本情報</h2>
            <div className={styles.row}>
              {renderField('name', '氏名', { required: true, placeholder: '山田 太郎' })}
              {renderField('name_kana', 'フリガナ', { placeholder: 'ヤマダ タロウ' })}
            </div>
            {renderField('birth_date', '生年月日', { type: 'date' })}
            <div className={styles.row}>
              {renderField('phone', '電話番号', { required: true, phone: true, placeholder: '090-1234-5678' })}
              {renderField('email', 'メールアドレス', { type: 'email', placeholder: 'example@mail.com' })}
            </div>
            {renderField('current_address', '現住所', { placeholder: '東京都渋谷区...' })}
          </div>
        )}

        {/* Step 2: 勤務先情報 */}
        {step === 1 && (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>🏢 勤務先情報</h2>
            {renderField('company_name', '会社名', { placeholder: '株式会社...' })}
            {renderField('company_address', '会社住所', { placeholder: '東京都...' })}
            <div className={styles.row}>
              {renderField('company_phone', '会社電話', { phone: true, placeholder: '03-1234-5678' })}
              <div className={styles.field}>
                <label className={styles.label}>年収</label>
                <select className={styles.input} value={form.annual_income} onChange={e => update('annual_income', e.target.value)}>
                  {INCOME_OPTIONS.map(o => <option key={o} value={o}>{o || '選択してください'}</option>)}
                </select>
              </div>
            </div>
            {renderField('years_employed', '勤続年数', { placeholder: '3年' })}
          </div>
        )}

        {/* Step 3: 連帯保証人 */}
        {step === 2 && (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>🤝 連帯保証人</h2>
            <p className={styles.stepHint}>保証会社を利用する場合は空欄で構いません</p>
            <div className={styles.row}>
              {renderField('guarantor_name', '氏名', { placeholder: '山田 花子' })}
              {renderField('guarantor_phone', '電話番号', { phone: true, placeholder: '090-1234-5678' })}
            </div>
            {renderField('guarantor_address', '住所', { placeholder: '東京都...' })}
            {renderField('guarantor_company', '勤務先', { placeholder: '株式会社...' })}
          </div>
        )}

        {/* Step 4: 入居情報 + 確認 */}
        {step === 3 && (
          <div className={styles.stepContent}>
            <h2 className={styles.stepTitle}>🏠 入居情報</h2>
            <div className={styles.row}>
              {renderField('move_in_date', '入居希望日', { type: 'date' })}
              <div className={styles.field}>
                <label className={styles.label}>入居人数</label>
                <select className={styles.input} value={form.num_residents} onChange={e => update('num_residents', e.target.value)}>
                  {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={String(n)}>{n}名</option>)}
                </select>
              </div>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>備考・要望</label>
              <textarea className={styles.textarea} value={form.notes} onChange={e => update('notes', e.target.value)} rows={3} placeholder="ペット飼育希望、駐車場希望など" />
            </div>

            {/* 入力内容確認 */}
            <div className={styles.confirmSection}>
              <h3 className={styles.confirmTitle}>入力内容の確認</h3>
              <div className={styles.confirmGrid}>
                {form.name && <div className={styles.confirmRow}><span className={styles.confirmLabel}>氏名</span><span>{form.name}</span></div>}
                {form.name_kana && <div className={styles.confirmRow}><span className={styles.confirmLabel}>フリガナ</span><span>{form.name_kana}</span></div>}
                {form.phone && <div className={styles.confirmRow}><span className={styles.confirmLabel}>電話番号</span><span>{form.phone}</span></div>}
                {form.email && <div className={styles.confirmRow}><span className={styles.confirmLabel}>メール</span><span>{form.email}</span></div>}
                {form.current_address && <div className={styles.confirmRow}><span className={styles.confirmLabel}>現住所</span><span>{form.current_address}</span></div>}
                {form.company_name && <div className={styles.confirmRow}><span className={styles.confirmLabel}>勤務先</span><span>{form.company_name}</span></div>}
                {form.annual_income && <div className={styles.confirmRow}><span className={styles.confirmLabel}>年収</span><span>{form.annual_income}</span></div>}
                {form.guarantor_name && <div className={styles.confirmRow}><span className={styles.confirmLabel}>保証人</span><span>{form.guarantor_name}</span></div>}
                {form.move_in_date && <div className={styles.confirmRow}><span className={styles.confirmLabel}>入居希望日</span><span>{form.move_in_date}</span></div>}
                {form.num_residents && <div className={styles.confirmRow}><span className={styles.confirmLabel}>入居人数</span><span>{form.num_residents}名</span></div>}
              </div>
            </div>
          </div>
        )}

        {/* ナビゲーションボタン */}
        <div className={styles.navButtons}>
          {step > 0 && (
            <button className={styles.backBtn} onClick={handleBack} type="button">← 戻る</button>
          )}
          <div className={styles.navSpacer} />
          {!isLast ? (
            <button className={styles.nextBtn} onClick={handleNext} type="button">
              次へ →
            </button>
          ) : (
            <button className={styles.submitBtn} onClick={handleSubmit} disabled={submitting} type="button">
              {submitting ? '送信中...' : '申込む'}
            </button>
          )}
        </div>

        <div className={styles.stepCounter}>
          {step + 1} / {STEPS.length}
        </div>
      </div>
    </div>
  );
}
